import type Luminary from '@lumen5/luminary';

import type { AppDispatch } from 'js/src/libs/RootState';
import type {
    MakeOperationsCallable,
    RecordOfNotCallableOperations,
} from 'js/src/luminary/luminary-hooks';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import type { CacheRef, GetRootState } from 'js/src/luminary/luminary-redux-connect-types';

/**
 * bindLuminaryOperations attempts to be the equivalent of the react-redux `bindActionCreators` method.  It
 * binds luminary operations (see luminary-operations.ts) to the luminary instance and redux dispatch
 * method, making it simple to call from a component.
 *
 * Sample code:
 * ```
 *  const mapLuminaryOperationsToProps = (dispatch: Dispatch, getState: GetRootState, luminary: Luminary) => {
 *      return bindLuminaryOperations({
 *          setColor,
 *      }, dispatch, getState, luminary);
 * };
 * ```
 *
 * @param operations - map of luminary operations
 * @param dispatch - The Redux `store.dispatch` method
 * @param getState - The Redux `store.getState` method
 * @param luminary - The Luminary instance
 */
export function bindLuminaryOperations<OperationsToBind extends RecordOfNotCallableOperations>(
    operations: OperationsToBind,
    dispatch: AppDispatch,
    getState: GetRootState,
    luminary: Luminary,
): MakeOperationsCallable<OperationsToBind> {
    return makeOperationsCallable(operations, luminary, dispatch, getState);
}

export type MapLuminaryStateToProps<Props> = (
    luminary: Luminary,
    ownProps: Partial<Props>,
    cacheRef: CacheRef,
    getState: GetRootState,
) => unknown;
